import { Component, Input, Output, EventEmitter, ViewChild, OnInit, OnChanges, SimpleChanges, AfterViewInit, inject, TemplateRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { DragDropModule, CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { AccordionModule } from 'primeng/accordion';
import { DatePickerModule } from 'primeng/datepicker';
import { SelectModule } from 'primeng/select';
import { MultiSelectModule } from 'primeng/multiselect';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { TableModule, Table } from 'primeng/table';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { DialogModule } from 'primeng/dialog';
import { DrawerModule } from 'primeng/drawer';
import { CheckboxModule } from 'primeng/checkbox';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { TagModule } from 'primeng/tag';
import  moment  from 'moment';
import { getSvgIcon } from '../utils/svg-icons.utils';
import { ColumnPreferenceService } from '../services/column-preference/column-preference.service';
import { lastValueFrom } from 'rxjs';
import { mapModuleNameToBackend } from '../utils/module-name-mapper';

// Interfaces
export interface FilterConfig {
  key: string;
  type: 'text' | 'date' | 'select' | 'number' | 'autocomplete' | 'multiselect';
  placeholder?: string;
  label?: string;
  options?: any[];
  suggestions?: any[];
  optionLabel?: string;
  optionValue?: string;
  value?: any;
  showInAccordion?: boolean;
  showInDialog?: boolean;
  showInSidebar?: boolean;
  // For lazy loaded selects
  lazy?: boolean;
  filter?: boolean;
  virtualScroll?: boolean;
  virtualScrollItemSize?: number;
  filterPlaceholder?: string;
  loading?: boolean;
  onFilter?: (event: any) => void;
  onLazyLoad?: (event: any) => void;
  onChange?: (event: any) => void;
  // For autocomplete/searchable selects
  completeMethod?: (event: { query: string; filter: FilterConfig }) => void | Promise<void>;
  dropdown?: boolean;
  forceSelection?: boolean;
}

export interface ColumnConfig {
  key: string;
  header: string;
  type?: 'text' | 'date' | 'datetime' | 'currency' | 'number' | 'status' | 'custom';
  format?: string;
  width?: string;
  align?: 'left' | 'center' | 'right';
  ellipsis?: boolean;
  customTemplate?: string;
  customField?: boolean;
  is_selected?: boolean;
  statusConfig?: {
    [key: string]: string | undefined;
  };
}
 
export interface ActionConfig {
  key: string;
  label: string;
  icon?: string;
  svgIcon?: string;
  tooltip?: string;
  severity?: 'primary' | 'secondary' | 'success' | 'info' | 'danger' | 'help' | 'contrast';
  show?: (row: any) => boolean;
  disabled?: (row: any) => boolean;
  disabledTooltip?: string | ((row: any) => string);
  type?: 'button' | 'toggle';
}

export interface FilterEvent {
  data?: any;
  filterKey?: string;
  searchValue?: string;
  filter?: FilterConfig;
}

export interface ActionEvent {
  action: string;
  row: any;
  rowIndex: number;
}

@Component({
  selector: 'lib-dynamic-listing',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DragDropModule,
    AccordionModule,
    DatePickerModule,
    SelectModule,
    MultiSelectModule,
    AutoCompleteModule,
    TableModule,
    InputTextModule,
    ButtonModule,
    TooltipModule,
    DialogModule,
    DrawerModule,
    CheckboxModule,
    ToggleSwitchModule,
    AutoCompleteModule,
    TagModule
  ],
  templateUrl: './dynamic-listing.html',
  styleUrl: './dynamic-listing.css',
})
export class DynamicListingComponent implements OnInit, OnChanges, AfterViewInit {
  @ViewChild('dt') dt!: Table;

  private readonly cellTooltipThreshold = 100;

  private sanitizer = inject(DomSanitizer);
  private columnPreferenceService = inject(ColumnPreferenceService);

  // Inputs
  @Input() module: string = '';
  @Input() filters: FilterConfig[] = [];
  @Input() columns: ColumnConfig[] = [];
  @Input() data: any[] = [];
  @Input() actions: ActionConfig[] = [];
  @Input() headerActions: ActionConfig[] = [];
  @Input() isLoading: boolean = false;
  @Input() pageIndex: number = 1;
  @Input() rowsPerPage: number = 10;
  @Input() totalCount: number = 0;
  @Input() totalPages: number = 0;
  @Input() exportFilename: string = 'export';
  @Input() showExportButtons: boolean = true;
  @Input() showPagination: boolean = true;
  @Input() showIncludeInactiveCheckbox: boolean = false;
  @Input() columnPreferenceId?: string;
  @Input() cellTemplates: Record<string, TemplateRef<any>> = {};
  @Input() showSerialNumber: boolean = true;
  @Input() showCheckbox: boolean = false;
  @Input() selectedRowIds: Set<string> = new Set();
  @Input() selectAll: boolean = false;
  private _includeInactive: boolean = false;
  
  @Input() 
  set includeInactive(value: boolean) {
    if (this._includeInactive !== value) {
      this._includeInactive = Boolean(value); // Ensure it's a boolean
      this.internalIncludeInactive = Boolean(value);
    }
  }
  
  get includeInactive(): boolean {
    return this._includeInactive;
  }

  // Outputs
  @Output() filterEvent = new EventEmitter<FilterEvent>();
  @Output() actionClicked = new EventEmitter<ActionEvent>();
  @Output() pageChanged = new EventEmitter<number>();
  @Output() exportCsv = new EventEmitter<void>();
  @Output() exportXlsx = new EventEmitter<void>();
  @Output() exportPdf = new EventEmitter<void>();
  @Output() columnsSelected = new EventEmitter<string[]>();
  @Output() includeInactiveChanged = new EventEmitter<boolean>();
  @Output() columnPreferenceSaved = new EventEmitter<{ id: string; columns: ColumnConfig[] }>();
  @Output() rowSelected = new EventEmitter<{ id: string; checked: boolean }>();
  @Output() selectAllChanged = new EventEmitter<boolean>();

  // Internal state
  filterValues: { [key: string]: any } = {};
  displayFilterDialog: boolean = false;
  displayFilterSidebar: boolean = false;
  filtersExpanded: boolean = true;
  selectedColumns: string[] = []; // Track selected column keys
  internalIncludeInactive: boolean = false; // Internal state for checkbox
  orderedColumns: ColumnConfig[] = []; // Track columns in display order
  activeCellTooltip?: { text: string; x: number; y: number };

  ngOnInit() {
    this.initializeFilterValues();
    this.initializeSelectedColumns();
    this.internalIncludeInactive = Boolean(this.includeInactive);
  }

  private initializeSelectedColumns() {
    // Initialize selected columns based on is_selected field from column config
    // If is_selected is not provided, default to true (all columns selected)
    this.selectedColumns = this.columns
      .filter(col => col.is_selected !== false) // Include columns where is_selected is true or undefined
      .map(col => col.key);
    
    // Initialize ordered columns with all columns in their original order
    this.resetColumnOrder();
  }


  ngAfterViewInit() {
    // Ensure checkbox value is set after view initialization
    this.internalIncludeInactive = Boolean(this.includeInactive);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['filters']) {
      // Preserve existing values when filters change
      const oldValues = { ...this.filterValues };
      this.initializeFilterValues();
      // Restore values that still exist
      this.filters.forEach(filter => {
        if (oldValues[filter.key] !== undefined) {
          this.filterValues[filter.key] = oldValues[filter.key];
        }
      });
    }
    if (changes['columns']) {
      // Initialize selected columns when columns change
      this.initializeSelectedColumns();
      // Update ordered columns
      this.resetColumnOrder();
    }

    if(changes['module']) {
      this.loadModuleAllowedColumns();
    }
  }

  loadModuleAllowedColumns() {
    if (!this.module) {
      this.selectedColumns = this.columns.map(col => col.key);
      this.resetColumnOrder();
      return;
    }

    const backendModuleName = mapModuleNameToBackend(this.module);
    this.columnPreferenceService.getByModuleName(backendModuleName).subscribe({
      next: (res) => {
        const pref = res?.data;
        if (pref?.columns_list && Array.isArray(pref.columns_list)) {
          this.columnPreferenceId = pref.id != null ? String(pref.id) : undefined;

          // Build a map of saved column configs (order + selection)
          const savedMap = new Map<string, any>();
          pref.columns_list.forEach((col: any) => savedMap.set(col.key, col));

          // Merge: use saved order and selection, but keep current column definitions
          const currentMap = new Map(this.columns.map(col => [col.key, col]));
          this.orderedColumns = [];

          // First add columns in saved order
          for (const saved of pref.columns_list) {
            const current = currentMap.get(saved.key);
            if (current) {
              this.orderedColumns.push(current);
            }
          }
          // Then add any new columns not in saved preference
          for (const col of this.columns) {
            if (!savedMap.has(col.key)) {
              this.orderedColumns.push(col);
            }
          }

          // Set selected columns based on saved is_selected
          this.selectedColumns = this.orderedColumns
            .filter(col => {
              const saved = savedMap.get(col.key);
              return saved ? saved.is_selected !== false : true;
            })
            .map(col => col.key);
        } else {
          // No preference found — show all columns
          this.selectedColumns = this.columns.map(col => col.key);
          this.resetColumnOrder();
        }
      },
      error: () => {
        // Fallback — show all columns
        this.selectedColumns = this.columns.map(col => col.key);
        this.resetColumnOrder();
      },
    });
  }

  private initializeFilterValues() {

    this.filters.forEach(filter => {
      if (filter.value !== undefined) {
        this.filterValues[filter.key] = filter.value;
      } else {
        this.filterValues[filter.key] = filter.type === 'date' ? null : '';
      }
    });
  }

  applyFilters() {
    const filterData: any = {};
    this.filters.forEach(filter => {
      filterData[filter.key] = this.filterValues[filter.key];
    });
    
    this.filterEvent.emit({
      data: filterData
    });

    this.displayFilterDialog = false;
    this.displayFilterSidebar = false; // Close sidebar when filters are applied
  }

  async applyColumnSelection() {
    // Build payload with columns in order, including is_selected based on selection
    const columnsPayload = this.orderedColumns.map(col => {
      const isSelected = this.selectedColumns.includes(col.key);
      return {
        key: col.key,
        header: col.header,
        type: col.type,
        ellipsis: col.ellipsis,
        align: col.align,
        is_selected: isSelected,
        customField: col.customField,
      };
    });

    // Check if we should update or create
    const shouldUpdate = this.columnPreferenceId && 
                         this.columnPreferenceId !== '0' && 
                         this.columnPreferenceId !== '';

    try {
      let response;
      if (shouldUpdate && this.columnPreferenceId) {
        // Update existing preference
        const backendModuleName = this.module ? mapModuleNameToBackend(this.module) : undefined;
        response = await lastValueFrom(
          this.columnPreferenceService.updateColumnPreference(this.columnPreferenceId, {
            columns_list: columnsPayload
          }, backendModuleName)
        );
      } else {
        // Create new preference
        if (!this.module) {
          console.error('Module name is required to create column preference');
          this.columnsSelected.emit([...this.selectedColumns]);
          this.displayFilterSidebar = false;
          return;
        }
        const backendModuleName = mapModuleNameToBackend(this.module);
        response = await lastValueFrom(
          this.columnPreferenceService.createColumnPreference({
            module_name: backendModuleName,
            columns_list: columnsPayload
          })
        );
      }

      // Emit event with the saved preference ID and columns
      if (response?.data) {
        // Ensure ID is always a string to prevent data loss with large numbers
        const savedId = response.data.id != null ? String(response.data.id) : (this.columnPreferenceId || '');
        this.columnPreferenceId = savedId; // Track internally for subsequent updates
        this.columnPreferenceSaved.emit({
          id: savedId,
          columns: columnsPayload
        });
      }

      // Also emit the selected columns for backward compatibility
      this.columnsSelected.emit([...this.selectedColumns]);
      this.displayFilterSidebar = false; // Close drawer
    } catch (error) {
      console.error('Error saving column preference:', error);
      // Still emit selected columns even if save fails
      this.columnsSelected.emit([...this.selectedColumns]);
      this.displayFilterSidebar = false;
    }
  }

  onIncludeInactiveChange(value: boolean) {
    this.internalIncludeInactive = value;
    this.includeInactiveChanged.emit(value);
  }

  toggleColumnSelection(columnKey: string, isSelected: boolean) {
    if (isSelected) {
      if (!this.selectedColumns.includes(columnKey)) {
        // Add the column at the position it appears in orderedColumns
        const columnIndex = this.orderedColumns.findIndex(col => col.key === columnKey);
        const selectedBefore = this.orderedColumns
          .slice(0, columnIndex)
          .filter(col => this.selectedColumns.includes(col.key))
          .map(col => col.key);
        const selectedAfter = this.orderedColumns
          .slice(columnIndex + 1)
          .filter(col => this.selectedColumns.includes(col.key))
          .map(col => col.key);
        
        this.selectedColumns = [...selectedBefore, columnKey, ...selectedAfter];
      }
    } else {
      // Remove from selectedColumns but keep in orderedColumns
      this.selectedColumns = this.selectedColumns.filter(key => key !== columnKey);
    }
  }

  isColumnSelected(columnKey: string): boolean {
    return this.selectedColumns.includes(columnKey);
  }

  getVisibleColumns(): ColumnConfig[] {
    // Return columns in the order of selectedColumns array
    const columnMap = new Map(this.columns.map(col => [col.key, col]));
    return this.selectedColumns
      .map(key => columnMap.get(key))
      .filter((col): col is ColumnConfig => col !== undefined);
  }

  getOrderedColumnsForPreference(): ColumnConfig[] {
    // Return columns in the order maintained by orderedColumns
    return this.orderedColumns;
  }

  dropColumn(event: CdkDragDrop<ColumnConfig[]>) {
    // Reorder the columns in orderedColumns
    moveItemInArray(this.orderedColumns, event.previousIndex, event.currentIndex);
    
    // Update selectedColumns to match the new order (only for selected columns)
    // This ensures selected columns maintain their new order
    const selectedInOrder = this.orderedColumns
      .filter(col => this.selectedColumns.includes(col.key))
      .map(col => col.key);
    
    // Update selectedColumns maintaining the new order
    this.selectedColumns = selectedInOrder;
  }

  clearFilters() {
    this.filters.forEach(filter => {
      this.filterValues[filter.key] = filter.type === 'date' ? null : '';
    });
    this.applyFilters();
  }

  clearColumnSelection() {
    this.selectedColumns = [];
  }

  fillColumnSelection() {
    this.selectedColumns = this.orderedColumns.map(col => col.key);
  }

  resetColumnOrder() {
    this.orderedColumns = [...this.columns];
  }

  toggleFilters() {
    this.filtersExpanded = !this.filtersExpanded;
  }

  openFilterSidebar() {
    this.displayFilterSidebar = true;
  }

  closeFilterSidebar() {
    this.displayFilterSidebar = false;
  }

  getFiltersForAccordion(): FilterConfig[] {
    return this.filters.filter(f => f.showInAccordion !== false);
  }

  getFiltersForDialog(): FilterConfig[] {
    return this.filters.filter(f => f.showInDialog === true);
  }

  onActionClick(action: ActionConfig, row: any, rowIndex: number) { // view, edit, delete button clicks
    // Check if action is disabled
    if (action.disabled && action.disabled(row)) {
      return; // Don't emit event for disabled actions
    }

    this.actionClicked.emit({
      action: action.key,
      row,
      rowIndex
    });
  }

  onHeaderActionClick(action: ActionConfig) {
    this.actionClicked.emit({
      action: action.key,
      row: null,
      rowIndex: -1
    });
  }

  loadPreviousPage() {
    if (this.pageIndex <= 1) return;
    this.pageChanged.emit(this.pageIndex - 1);
  }

  loadNextPage() {
    if (this.pageIndex >= this.totalPages) return;
    this.pageChanged.emit(this.pageIndex + 1);
  }

  onExportCsv() {
    
    console.log('Export CSV dynamic-listing clicked');

    this.exportCsv.emit();
  }

  onExportXlsx() {

    console.log('Export XLSX dynamic-listing clicked');

    this.exportXlsx.emit();
  }

  onExportPdf() {
    console.log('Export PDF dynamic-listing clicked');

    this.exportPdf.emit();
  }

  formatCellValue(row: any, column: ColumnConfig, isCustomField?: boolean): string { // returns formatted string value

    let value = row[column.key];
    if (isCustomField) {
      // Support both nested custom_field/custom_fields object and spread custom fields on the row
      const customValue = row.custom_field?.[column.key] ?? row.custom_fields?.[column.key] ?? row[column.key];
      if (customValue === null || customValue === undefined) {
        return '-';
      }
      return customValue?.toString() || '-';
    } 
    
    if (value === null || value === undefined) {
      return '-';
    }

    // Handle datetime type - format with date and time
    if (column.type === 'datetime') {
      if (moment(value, moment.ISO_8601, true).isValid()) {
        return moment(value).format('DD/MM/YYYY HH:mm a');
      }
      // Try parsing as date if not ISO format
      const dateValue = moment(value);
      if (dateValue.isValid()) {
        return dateValue.format('DD/MM/YYYY HH:mm a');
      }
      return value?.toString() || '-';
    }

    // Handle date type - format as DD/MM/YYYY
    if (column.type === 'date') {
      if (moment(value, moment.ISO_8601, true).isValid()) {
        return moment(value).format('DD/MM/YYYY');
      }
      // Try parsing as date if not ISO format
      const dateValue = moment(value);
      if (dateValue.isValid()) {
        return dateValue.format('DD/MM/YYYY');
      }
      return value?.toString() || '-';
    }

    // Handle currency type
    if (column.type === 'currency') {
      const numValue = parseFloat(value);
      if (!isNaN(numValue)) {
        return new Intl.NumberFormat('en-IN', {
          style: 'currency',
          currency: 'INR',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(numValue);
      }
      return value?.toString() || '-';
    }

    // Handle number type
    if (column.type === 'number') {
      const numValue = parseFloat(value);
      if (!isNaN(numValue)) {
        return numValue.toLocaleString('en-IN');
      }
      return value?.toString() || '-';
    }

    // Check if this column should have snake_case converted to Title Case
    if (this.STATUS_COLUMNS.includes(column.header)) {
      return this.convertSnakeCaseToTitleCase(value?.toString() || '-');
    }

    return value?.toString() || '-';
  }

  getCellDisplayMeta(row: any, column: ColumnConfig, isCustomField?: boolean): {
    text: string;
    tooltip?: string;
    truncated: boolean;
  } {
    const value = this.formatCellValue(row, column, isCustomField);

    if (value === '-' || value.length <= this.cellTooltipThreshold) {
      return {
        text: value,
        tooltip: undefined,
        truncated: false,
      };
    }

    return {
      text: `${value.slice(0, this.cellTooltipThreshold)}...`,
      tooltip: value,
      truncated: true,
    };
  }



  
  // Sanitize and return SVG HTML for safe rendering
  getSanitizedSvg(svgString: string | undefined): SafeHtml | null {
    if (!svgString) {
      return null;
    }
    // Use bypassSecurityTrustHtml for SVG content as it's trusted content from our utils
    return this.sanitizer.bypassSecurityTrustHtml(svgString);
  }

  // Get column preference icon
  getColumnPreferenceIcon(): SafeHtml | null {
    const icon = getSvgIcon('COLUMN_PREFERENCE');
    return this.getSanitizedSvg(icon);
  }

  openFilterDialog($event: any) {
    $event.stopPropagation();
    this.displayFilterDialog = true;
  }

  /**
   * Handle dropdown click - trigger search with empty query to show all options
   */
  async onAutocompleteDropdownClick(event: any, filter: FilterConfig) {
    if (filter.completeMethod) {
      try {
        // Trigger search with empty query to show all options
        await filter.completeMethod({ query: '', filter });
        // Update the filter's suggestions after the search
        const filterIndex = this.filters.findIndex(f => f.key === filter.key);
        if (filterIndex !== -1) {
          this.filters[filterIndex].suggestions = filter.suggestions || [];
        }
      } catch (error) {
        console.error('Error in autocomplete dropdown click:', error);
      }
    }
  }

  /**
   * Handle Enter key press - trigger search with current input value
   */
  async onAutocompleteEnterKey(event: any, filter: FilterConfig) {
    if (filter.completeMethod) {
      try {
        // Get the current input value
        const inputElement = event.target as HTMLInputElement;
        const query = inputElement?.value || '';
        
        // Trigger search with the current query
        await filter.completeMethod({ query, filter });
        // Update the filter's suggestions after the search
        const filterIndex = this.filters.findIndex(f => f.key === filter.key);
        if (filterIndex !== -1) {
          this.filters[filterIndex].suggestions = filter.suggestions || [];
        }
      } catch (error) {
        console.error('Error in autocomplete Enter key search:', error);
      }
    }
  }

  /**
   * Handle typeahead search - trigger search as user types
   * This is called by PrimeNG's completeMethod event
   */
  async onAutocompleteTypeahead(event: { query: string }, filter: FilterConfig) {
    if (filter.completeMethod) {
      try {
        const query = event.query || '';
        
        // Trigger search with the current query
        await filter.completeMethod({ query, filter });
        // Update the filter's suggestions after the search
        const filterIndex = this.filters.findIndex(f => f.key === filter.key);
        if (filterIndex !== -1) {
          this.filters[filterIndex].suggestions = filter.suggestions || [];
        }
      } catch (error) {
        console.error('Error in autocomplete typeahead search:', error);
      }
    }
  }

  onAutocompleteSelect(event: any, filter: FilterConfig) {
    // Update filter value when an option is selected
    // PrimeNG autocomplete returns the full selected object, so we need to extract the value
    let filterValue = event;
    if (event && typeof event === 'object' && event !== null) {
      // Check if it has a value property (autocomplete option format: { value: id, label: name })
      if (event.value !== undefined && event.value !== null) {
        filterValue = event.value;
      } else if (event.id !== undefined && event.id !== null) {
        filterValue = event.id;
      } else {
        // If no value/id found, try to extract from optionValue if specified
        if (filter.optionValue && event[filter.optionValue] !== undefined) {
          filterValue = event[filter.optionValue];
        } else {
          // Last resort: keep the object but this might cause issues - log a warning
          console.warn(`Autocomplete filter "${filter.key}" selected object without value/id property:`, event);
          filterValue = event;
        }
      }
    }
    this.filterValues[filter.key] = filterValue;
    // Optionally trigger filter application
    if (filter.onChange) {
      filter.onChange(event);
    }
    // Auto-apply filters when a selection is made (for better UX) 
    this.applyFilters();
  }

  onAutocompleteClear(filter: FilterConfig) {
    // Clear the filter value when the clear button is clicked
    this.filterValues[filter.key] = null;
    // Optionally trigger filter application
    if (filter.onChange) {
      filter.onChange(null);
    }
    // Auto-apply filters when cleared (for better UX)
    this.applyFilters();
  }

  /**
   * Array of column headers that should have their values converted from snake_case to Title Case
   * Example: "not_required" → "Not Required"
   */
  private readonly STATUS_COLUMNS = [
    'Approval Status',
    'Mapping Status',
    'Payment Status',
    'Accounting Status',
    'Status'
  ];

  /**
   * Convert snake_case string to Title Case
   * Example: "not_required" → "Not Required", "partially_mapped" → "Partially Mapped"
   */
  onRowCheckboxChange(row: any, checked: boolean): void {
    this.rowSelected.emit({ id: String(row.id), checked });
  }

  onSelectAllChange(checked: boolean): void {
    this.selectAllChanged.emit(checked);
  }

  isRowSelected(row: any): boolean {
    return this.selectedRowIds.has(String(row.id));
  }

  private convertSnakeCaseToTitleCase(value: string): string {
    if (!value || typeof value !== 'string') {
      return value;
    }
    
    return value
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

}
